import { isApiError } from "@core/api/api-error";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { useQueryParams } from "@core/routing/use-query-params";
import { useRouteParams } from "@core/routing/use-route-params";
import { useFormatPatients, usePatient } from "@domain/patient/hooks/use-patients";
import { isPendingPatient } from "@domain/patient/patient";
import { sendAnalyticsEventSwitchPatientDetail } from "@domain/program/program-analytics";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { NetworkError } from "@ui/components/common/error";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath, TabPatientDetails } from "@ui/navigation/routes";
import { HealthcareTeamTab } from "@ui/pages/patient-details/components/healthcare-team-tab/healthcare-team-tab";
import { PatientDetailsColumn } from "@ui/pages/patient-details/components/patient-details/patient-details-column";
import { ProgramTab } from "@ui/pages/patient-details/components/program-tab/program-tab";
import { ProgressTab } from "@ui/pages/patient-details/components/progress-tab/progress-tab";
import { BackButton } from "@ui/pages/register/components/back-button";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint } from "@ui/style";
import { colors } from "@ui/style/colors";
import { row, stack } from "@ui/style/layout";
import { isNotNil } from "@utils/assert";
import { BreadCrumb } from "primereact/breadcrumb";
import { TabPanel, TabView } from "primereact/tabview";
import React from "react";

export const PatientDetailsPage: React.FC = () => {
	const { format } = useI18n();
	const { formatPatientName } = useFormatPatients();
	const { patientId } = useRouteParams<RoutesPath.PatientDetails>();
	const { active, programId } = useQueryParams<RoutesPath.PatientDetails>();
	const { loading, patient, error, refreshPatient } = usePatient(patientId);
	const navigate = useNavigate();
	const { isDesktop } = useDimensions();

	if (!patient) {
		if (loading) {
			return <LoadingContainer />;
		}
		return (
			<Container>
				<NetworkError
					description={isApiError(error) ? format("global.patientNotFound.description") : undefined}
					onRefresh={async () => {
						patientId && (await refreshPatient(patientId));
					}}
				/>
			</Container>
		);
	}

	return (
		<>
			<Header>
				{!isDesktop && <BackButton onClick={() => navigate(RoutesPath.Home)} />}
				<StyledBreadCrumb
					model={[
						{
							label: format("patientDetails.breadCrumb.home"),
							url: RoutesPath.Home,
							command: e => {
								e.originalEvent.preventDefault();
								navigate(RoutesPath.Home);
							},
						},
						{
							label: formatPatientName(patient),
							command: e => {
								e.originalEvent.preventDefault();
							},
						},
					]}
				/>
			</Header>
			<ContentContainer isDesktop={isDesktop}>
				<PatientDetailsColumn patient={patient} />
				<ProgramsSection>
					{isPendingPatient(patient) ? (
						<StyledTabView
							activeIndex={isNotNil(active) ? pendingPatientTabNames.indexOf(active) : 0}
							onTabChange={() =>
								navigate(RoutesPath.PatientDetails, {
									route: { patientId: patient.id },
									query: { active: TabPatientDetails.PROGRAMS_TAB },
								})
							}
						>
							<TabPanel header={format("patientDetails.tabs.programs")}>
								<ProgramTab patient={patient} />
							</TabPanel>
						</StyledTabView>
					) : (
						<StyledTabView
							activeIndex={isNotNil(active) ? patientTabNames.indexOf(active) : 0}
							onTabChange={e => {
								navigate(RoutesPath.PatientDetails, {
									route: { patientId: patient.id },
									query: { active: patientTabNames[e.index], programId: programId },
								});
								sendAnalyticsEventSwitchPatientDetail(patient.analyticsId, patientTabNames[e.index]);
							}}
						>
							<TabPanel className={e2eTestId.patientPage.progressTab} header={format("patientDetails.tabs.progress")}>
								<ProgressTab patient={patient} />
							</TabPanel>
							<TabPanel className={e2eTestId.patientPage.ProgramTab} header={format("patientDetails.tabs.programs")}>
								<ProgramTab patient={patient} />
							</TabPanel>
							<TabPanel
								className={e2eTestId.patientPage.healthcareTeamTab}
								header={format("patientDetails.tabs.healthcareTeam")}
							>
								<HealthcareTeamTab patient={patient} />
							</TabPanel>
						</StyledTabView>
					)}
				</ProgramsSection>
			</ContentContainer>
		</>
	);
};

const patientTabNames = [
	TabPatientDetails.PROGRESS_TAB,
	TabPatientDetails.PROGRAMS_TAB,
	TabPatientDetails.HEALTHCARE_TAB,
];

const pendingPatientTabNames = [TabPatientDetails.PROGRAMS_TAB];

const Container = styled.div`
	${stack(undefined, "center", "stretch")};
	flex-grow: 1;
`;

const Header = styled.div`
	${row(undefined, undefined, "center")}
	margin: 16px 24px;

	@media ${physioMeTabletBreakpoint} {
		margin: 16px;
	}
`;

const ContentContainer = styled.div<{ isDesktop: boolean }>`
	${({ isDesktop }) => isDesktop && row(24, "flexstart", "stretch")};
	flex-grow: 1;
	margin: 0 24px;

	@media ${physioMeTabletBreakpoint} {
		margin: 0;
		background-color: ${colors.white};
	}
`;

const ProgramsSection = styled.section`
	flex: 1;
	background-color: ${colors.white};
	border-radius: 16px 16px 0px 0px;
	padding: 24px 40px;
	min-width: 0;

	@media ${physioMeTabletBreakpoint} {
		border-radius: 0;
		padding: 0;
		flex-grow: 1;
	}
`;

const StyledBreadCrumb = styled(BreadCrumb)`
	margin-left: 16px;

	.p-menuitem-text {
		font-size: 0.75rem;
		font-weight: 600;
		color: ${colors.grey80};
	}

	li:last-child .p-menuitem-link {
		cursor: initial;
		color: ${colors.grey};
	}
`;

const StyledTabView = styled(TabView)`
	.p-tabview-nav {
		@media ${physioMeTabletBreakpoint} {
			background-color: ${colors.primaryBackground} !important;
		}
	}

	.p-tabview-nav-link {
		@media ${physioMeTabletBreakpoint} {
			background-color: ${colors.primaryBackground} !important;
			justify-content: center !important;
		}
	}

	.p-tabview-panels {
		@media ${physioMeTabletBreakpoint} {
			padding: 16px 32px !important;
		}
		@media ${physioMeMobileBreakpoint} {
			padding: 16px !important;
		}
	}
`;
